import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
  selectActiveHighlightRequest,
  setActiveHighlightRequest,
} from "../../DocumentPreviewSlice_Store";
import {
  highlightRequestToFactHighlights,
  getActiveHighlightId,
  buildHighlightRequest,
  type HighlightRequest,
  type FactHighlight,
  type FactHighlightSource,
} from "../../services/factPreviewService";
import { type CitationSearchProgress } from "../pdf/PdfFactViewer";
import { type DocumentData, type FactData } from "./documentPreviewTypes";

/** Give up on a citation search that has reported no progress for this long. */
const CITATION_SEARCH_TIMEOUT_MS = 10000;
/** Let the facts panel render before scrolling the active fact into view. */
const SCROLL_DELAY_MS = 100;

const DEFAULT_PANEL_HEIGHT = 180;
const MIN_PANEL_HEIGHT = 120;
const MAX_PANEL_FRACTION = 0.8;

interface CitationState {
  factHighlights: FactHighlight[];
  activeHighlightId: string | null;
  searchTrigger: number;
  isFindingCitation: boolean;
  citationSearchProgress: CitationSearchProgress | null;
  setFactHighlights: Dispatch<SetStateAction<FactHighlight[]>>;
  setActiveHighlightId: Dispatch<SetStateAction<string | null>>;
  setSearchTrigger: Dispatch<SetStateAction<number>>;
  setIsFindingCitation: Dispatch<SetStateAction<boolean>>;
  setCitationSearchProgress: Dispatch<SetStateAction<CitationSearchProgress | null>>;
}

export interface DocumentFacts {
  sortedDocumentFacts: FactData[];
  clickedFact: FactData | null;
  factIdToIndex?: Record<string, number>;
  isTabular: boolean;
  factHighlights: FactHighlight[];
  activeHighlightId: string | null;
  searchTrigger: number;
  isFindingCitation: boolean;
  citationSearchProgress: CitationSearchProgress | null;
  factsPanelHeight: number;
  factRowRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  onFactClick: (fact: FactData, factIndex: number) => void;
  onCitationSearchStart: (totalPages: number) => void;
  onCitationSearchProgress: (progress: CitationSearchProgress) => void;
  onCitationSearchComplete: () => void;
  onDividerDragMove: (clientY: number) => void;
}

function useCitationState(): CitationState {
  const [factHighlights, setFactHighlights] = useState<FactHighlight[]>([]);
  const [activeHighlightId, setActiveHighlightId] = useState<string | null>(null);
  // Monotonic counter: forces PdfFactViewer to re-search even when activeHighlightId is unchanged
  const [searchTrigger, setSearchTrigger] = useState<number>(0);
  const [isFindingCitation, setIsFindingCitation] = useState<boolean>(false);
  const [citationSearchProgress, setCitationSearchProgress] =
    useState<CitationSearchProgress | null>(null);

  return {
    factHighlights,
    activeHighlightId,
    searchTrigger,
    isFindingCitation,
    citationSearchProgress,
    setFactHighlights,
    setActiveHighlightId,
    setSearchTrigger,
    setIsFindingCitation,
    setCitationSearchProgress,
  };
}

function useCitationEffects(
  highlightRequest: HighlightRequest | null,
  docId: string | undefined,
  state: CitationState
): void {
  // Switching documents abandons any search that was running for the old one
  useEffect(() => {
    state.setIsFindingCitation(false);
    state.setCitationSearchProgress(null);
  }, [docId]);

  useEffect(() => {
    if (highlightRequest && !highlightRequest.isTabular) {
      state.setFactHighlights(highlightRequestToFactHighlights(highlightRequest));

      const activeId = getActiveHighlightId(highlightRequest);
      state.setActiveHighlightId(activeId);

      if (activeId) {
        state.setIsFindingCitation(true);
        state.setSearchTrigger((prev) => prev + 1);
      }
    } else {
      state.setFactHighlights([]);
      state.setActiveHighlightId(null);
      state.setIsFindingCitation(false);
      state.setCitationSearchProgress(null);
    }
  }, [highlightRequest]);

  // Safety net: clear stuck "finding" state after 10s of no progress
  useEffect(() => {
    if (!state.isFindingCitation) return;

    const safetyTimeout = setTimeout(() => {
      state.setIsFindingCitation(false);
      state.setCitationSearchProgress(null);
    }, CITATION_SEARCH_TIMEOUT_MS);

    return () => clearTimeout(safetyTimeout);
  }, [state.isFindingCitation, state.citationSearchProgress]);
}

/** Facts are shown in the order the chat numbered them; unnumbered facts sort last. */
function displayIndexOf(fact: FactData, factIdToIndex: Record<string, number>): number {
  return fact.factId ? (factIdToIndex[fact.factId] ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
}

function sortByDisplayIndex(
  facts: FactData[],
  factIdToIndex?: Record<string, number>
): FactData[] {
  if (!factIdToIndex || facts.length === 0) {
    return facts;
  }
  return [...facts].sort(
    (a, b) => displayIndexOf(a, factIdToIndex) - displayIndexOf(b, factIdToIndex)
  );
}

/** Same derivation buildHighlightRequest uses, applied to the newly clicked fact. */
function derivedSourceType(fact: FactData): "text" | "image" {
  return (
    (fact.factSourceType === "tabular" ? "text" : (fact.factSourceType as "text" | "image")) ||
    (fact.factFromImage ? "image" : "text")
  );
}

/** Clicking a fact row rebuilds the highlight request around that fact. */
function useFactSelection(
  allDocumentFacts: FactData[]
): (fact: FactData, factIndex: number) => void {
  const dispatch = useAppDispatch();

  return useCallback(
    (clickedFact: FactData, factIndex: number): void => {
      if (!allDocumentFacts || allDocumentFacts.length === 0) {
        return;
      }

      const factCompatibleObject: FactHighlightSource = {
        documentId: clickedFact.documentId,
        facts: allDocumentFacts as any,
      };
      const newHighlightRequest = buildHighlightRequest(factCompatibleObject, factIndex);

      if (newHighlightRequest) {
        newHighlightRequest.fact = clickedFact;
        newHighlightRequest.factId = clickedFact.factId;
        newHighlightRequest.allDocumentFacts = allDocumentFacts;
        newHighlightRequest.factSourceType = derivedSourceType(clickedFact);
      }

      dispatch(setActiveHighlightRequest(newHighlightRequest));
    },
    [allDocumentFacts, dispatch]
  );
}

function useFactsPanel(): {
  factsPanelHeight: number;
  onDividerDragMove: (clientY: number) => void;
} {
  const [factsPanelHeight, setFactsPanelHeight] = useState<number>(DEFAULT_PANEL_HEIGHT);

  const onDividerDragMove = useCallback((clientY: number): void => {
    const viewportHeight = window.innerHeight;
    const newHeight = viewportHeight - clientY;
    const maxHeight = viewportHeight * MAX_PANEL_FRACTION;
    setFactsPanelHeight(Math.max(MIN_PANEL_HEIGHT, Math.min(maxHeight, newHeight)));
  }, []);

  return { factsPanelHeight, onDividerDragMove };
}

function scrollFactIntoView(factElement: HTMLDivElement): void {
  const scrollContainer = factElement.closest(".overflow-y-auto");
  if (!scrollContainer) {
    factElement.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }

  // Centre the row inside the facts list
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = factElement.getBoundingClientRect();
  const scrollTop =
    scrollContainer.scrollTop +
    (elementRect.top - containerRect.top) -
    containerRect.height / 2 +
    elementRect.height / 2;
  scrollContainer.scrollTo({ top: Math.max(0, scrollTop), behavior: "smooth" });
}

/** Scrolls the clicked fact into view once loading has finished and rows exist. */
function useFactRowScroll(
  clickedFact: FactData | null,
  sortedDocumentFacts: FactData[],
  loading: boolean
): MutableRefObject<Record<string, HTMLDivElement | null>> {
  const factRowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (loading || !clickedFact?.factId || sortedDocumentFacts.length === 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const factElement = factRowRefs.current[clickedFact.factId];
      if (factElement) {
        scrollFactIntoView(factElement);
      }
    }, SCROLL_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [clickedFact, sortedDocumentFacts, loading]);

  return factRowRefs;
}

/**
 * Citation highlights and the facts panel for the document being previewed.
 *
 * InlineCitationBadge attaches ALL facts for the document, not just the clicked
 * one, so the panel can list them and switch between them.
 */
export function useDocumentFacts(document: DocumentData | null, loading: boolean): DocumentFacts {
  const highlightRequest = useAppSelector(selectActiveHighlightRequest) as HighlightRequest | null;
  const allDocumentFacts = (highlightRequest?.allDocumentFacts as FactData[] | undefined) || [];
  const clickedFact = (highlightRequest?.fact as FactData | undefined) || null;
  // Chat numbering map (factId -> display index)
  const factIdToIndex = highlightRequest?.factIdToIndex;

  const citation = useCitationState();
  useCitationEffects(highlightRequest, document?.docId, citation);

  const sortedDocumentFacts = useMemo(
    () => sortByDisplayIndex(allDocumentFacts, factIdToIndex),
    [allDocumentFacts, factIdToIndex]
  );
  const factRowRefs = useFactRowScroll(clickedFact, sortedDocumentFacts, loading);
  const panel = useFactsPanel();
  const onFactClick = useFactSelection(allDocumentFacts);

  const onCitationSearchStart = useCallback((totalPages: number): void => {
    citation.setIsFindingCitation(true);
    citation.setCitationSearchProgress({ currentPage: 0, totalPages, percentage: 0 });
  }, []);

  const onCitationSearchComplete = useCallback((): void => {
    citation.setIsFindingCitation(false);
    citation.setCitationSearchProgress(null);
  }, []);

  return {
    ...citation,
    ...panel,
    sortedDocumentFacts,
    clickedFact,
    factIdToIndex,
    isTabular: Boolean(highlightRequest?.isTabular),
    factRowRefs,
    onFactClick,
    onCitationSearchStart,
    onCitationSearchProgress: citation.setCitationSearchProgress,
    onCitationSearchComplete,
  };
}
